import React from 'react';
import { useRouter } from 'next/router';

import { Button, Logo } from 'components/ui';
import IconSearch from 'components/ui/icons/IconSearch';
import { useDrawerDispatchHelpers, useDrawerStore } from 'context/DrawerContext';
import { useSearchDispatchHelpers, useSearchStore } from 'context/SearchContext';
import { DRAWER_TYPES } from 'helpers/LayoutHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useAnalytics } from 'hooks/useAnalytics';

import HeaderCartButton from '../HeaderCartButton';
import MenuIcon from '../MenuIcon';

import SearchModal from './SearchModal';

import styles from './HeaderMobile.module.scss';

interface Props {
  hasInlineSearchBar?: boolean,
  isPinned?: boolean,
  hideCart?: boolean
}

const HeaderMobile = (props: Props) => {

  const {
    hasInlineSearchBar,
    isPinned,
    hideCart
  } = props;

  // Hooks

  const router = useRouter();

  const { toggleHeaderSearchBar } = useSearchDispatchHelpers();
  const { isHeaderSearchBarActive: isSearchActive } = useSearchStore();

  // Handlers

  const onSearchButtonClick = () => {
    toggleHeaderSearchBar(true);
  };

  const handleRouteChange = React.useCallback(() => {
    toggleHeaderSearchBar(false);
  }, [toggleHeaderSearchBar]);

  // Effects

  React.useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [handleRouteChange, router.events]);

  // Classes

  const classes = cx(
    styles.root,
    { [styles.searchVisible]: isSearchActive },
    { [styles.pinned]: isPinned },
  );

  return (
    <>

      {/* Header */}
      <div
        className={classes}
        data-testid="header-mobile"
      >
        <div className={styles.inner}>

          {/* MenuBar */}
          <div
            className={cx(styles.menuBar, { [styles.hidden]: isSearchActive })}
          >

            {/* Left Buttons */}
            <div className={cx(styles.buttonsContainer, styles.left)}>

              {/* Main Menu */}
              <MainMenuButton />

            </div>

            {/* Logo */}
            <Logo
              className={styles.logo}
              width={109}
              height={56}
              mobile
              data-testid="header-mobile-logo"
              hasHolidayIcon
            />

            {/* Right Buttons */}
            <div className={cx(styles.buttonsContainer, styles.right)}>

              {/* Search */}
              <SearchButton
                hasInlineSearchBar={!!hasInlineSearchBar}
                onClick={onSearchButtonClick}
              />

              {/* Cart */}
              {
                !hideCart && (
                  <div className={styles.btnWrapper}>
                    <HeaderCartButton className={styles.btn} isMobile />
                  </div>
                )
              }

            </div>
          </div>

        </div>
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchActive}
        onToggle={(isActive) => toggleHeaderSearchBar(isActive)}
      />
    </>
  );
};

// Buttons

// --- Main Menu Button

const MainMenuButton = () => {

  // Hooks

  const analytics = useAnalytics();

  const { showDrawer } = useDrawerDispatchHelpers();

  const { visible, type } = useDrawerStore();

  // Props

  const isVisible = type === DRAWER_TYPES.MENU_MOBILE && visible;

  // Handlers

  const handleClick = () => {
    showDrawer(
      DRAWER_TYPES.MENU_MOBILE,
      { title: 'HEADER.MOBILE.MAIN_MENU' },
    );
    if (!isVisible) {
      analytics.expandMainMenu();
    }
  };

  // Render

  return (
    <div
      className={styles.btnWrapper}
    >
      <Button
        className={cx(styles.btn, styles.menuBtn)}
        size="small"
        variant="primary"
        type="button"
        data-testid="main-menu-btn"
        aria-label="Menu"
        onClick={handleClick}
      >
        <MenuIcon open={isVisible} />
      </Button>
    </div>
  );
};

// --- Search Button

const SearchButton = (props: {hasInlineSearchBar: boolean, onClick: () => void}) => {
  const {
    hasInlineSearchBar,
    onClick
  } = props;

  const { isInlineSearchBarVisible } = useSearchStore();

  const classes = cx(
    styles.btn,
    styles.searchBtn,
    { [styles.hidden]: hasInlineSearchBar && isInlineSearchBarVisible }
  );

  return (
    <div className={styles.btnWrapper}>
      <Button
        className={classes}
        icon={IconSearch}
        iconProps={{ size: 22, strokeWidth: 2 }}
        size="small"
        variant="subtle"
        aria-label="Search"
        type="button"
        onClick={hasInlineSearchBar && isInlineSearchBarVisible ? undefined : onClick}
      />
    </div>
  );
};

// Export

export default React.memo(HeaderMobile);
